import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, TrendingUp, Clock, DollarSign, Activity, RefreshCw, CheckCircle, Info } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useBalance } from '../contexts/BalanceContext';
import CryptoIcon from './CryptoIcon';
import {
  CURRENCIES,
  fetchBalance as fetchBalanceService,
  fetchConversionRate as fetchRateService,
  fetchConversionHistory as fetchHistoryService,
  performConversion as performConversionService,
  formatDate,
  formatAmount
} from '../services/conversionService';

const EnhancedCryptoConversion = ({ onBalanceUpdate }) => {
  const { toast } = useToast();
  const { refreshBalance } = useBalance();
  
  // State management (matching Transfer.jsx exactly)
  const [currentView, setCurrentView] = useState('convert'); // 'convert', 'history'
  const [fromAsset, setFromAsset] = useState('USDT');
  const [toAsset, setToAsset] = useState('BTC');
  const [amount, setAmount] = useState('');
  const [balance, setBalance] = useState(null);
  const [conversionRate, setConversionRate] = useState(null);
  const [convertedAmount, setConvertedAmount] = useState(0);
  const [conversionHistory, setConversionHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchBalance();
    
    // Fetch conversion history if on history tab
    if (currentView === 'history') {
      fetchConversionHistory();
    }
  }, [currentView]);

  // Fetch conversion rate when assets change
  useEffect(() => {
    if (fromAsset && toAsset && fromAsset !== toAsset) {
      fetchConversionRate();
    }
  }, [fromAsset, toAsset]);

  // Calculate converted amount when rate or amount changes
  useEffect(() => {
    if (conversionRate && amount && !isNaN(amount) && parseFloat(amount) > 0) {
      const converted = parseFloat(amount) * conversionRate.rate;
      setConvertedAmount(converted);
    } else {
      setConvertedAmount(0);
    }
  }, [conversionRate, amount]);

  const fetchBalance = async () => {
    try {
      const data = await fetchBalanceService();
      console.log('Balance data:', data);
      setBalance(data);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const fetchConversionRate = async () => {
    try {
      const data = await fetchRateService(fromAsset, toAsset);
      console.log('Conversion rate:', data);
      setConversionRate(data);
    } catch (error) {
      console.error('Error fetching conversion rate:', error);
    }
  };

  const fetchConversionHistory = async () => {
    try {
      const conversions = await fetchHistoryService(20);
      console.log('Conversion history:', conversions);
      setConversionHistory(conversions);
    } catch (error) {
      console.error('Error fetching conversion history:', error);
    }
  };

  const handleConvert = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({ title: 'Error', description: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }

    if (fromAsset === toAsset) {
      toast({ title: 'Error', description: 'Cannot convert to same asset', variant: 'destructive' });
      return;
    }

    if (balance?.balances && balance.balances[fromAsset] < parseFloat(amount)) {
      toast({ title: 'Error', description: `Insufficient ${fromAsset} balance`, variant: 'destructive' });
      return;
    }

    setIsLoading(true);
    
    try {
      const data = await performConversionService(fromAsset, toAsset, amount);
      console.log('Conversion successful:', data);
      
      toast({
        title: '✅ Conversion Complete',
        description: `Successfully converted ${amount} ${fromAsset} to ${formatAmount(data.to_amount, toAsset)} ${toAsset}`,
      });

      // Refresh balance and switch to history
      await fetchBalance();
      await refreshBalance(); // Refresh global balance context
      setAmount('');
      setCurrentView('history');
      
      // Notify parent to update balance
      if (onBalanceUpdate) onBalanceUpdate();
    } catch (error) {
      console.error('Error converting:', error);
      toast({ 
        title: 'Conversion Failed', 
        description: error.message || 'Failed to process conversion', 
        variant: 'destructive' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const swapAssets = () => {
    const temp = fromAsset;
    setFromAsset(toAsset);
    setToAsset(temp);
  };

  const setMaxAmount = () => {
    if (balance?.balances && balance.balances[fromAsset]) {
      setAmount(balance.balances[fromAsset].toString());
    }
  };

  // Render convert tab
  const renderConvertTab = () => (
    <div className="space-y-6">{/*Stats removed - matching Transfer.jsx*/}

      <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-orange-500/30 rounded-xl p-6">
        {/* From Currency */}
        <div className="mb-4">
          <label className="text-sm text-gray-400 mb-2 block">From</label>
          <div className="bg-slate-900/50 border border-orange-500/20 rounded-lg p-4">
            <select
              value={fromAsset}
              onChange={(e) => setFromAsset(e.target.value)}
              className="w-full bg-transparent text-white outline-none mb-3"
            >
              {CURRENCIES.map(currency => (
                <option key={currency.symbol} value={currency.symbol} className="bg-slate-900">
                  {currency.name} ({currency.symbol})
                </option>
              ))}
            </select>
            
            <div className="flex items-center justify-between gap-4">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="flex-1 bg-transparent text-2xl font-bold text-white outline-none"
              />
              <button
                onClick={setMaxAmount}
                className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-lg text-sm hover:bg-orange-500/30 transition-colors"
              >
                MAX
              </button>
            </div>
            
            <div className="text-sm text-gray-400 mt-2">
              Balance: {balance?.balances ? (balance.balances[fromAsset] || 0).toFixed(6) : '0.00'} {fromAsset}
            </div>
          </div>
        </div>

        {/* Swap Button */}
        <div className="flex justify-center -my-2">
          <button
            onClick={swapAssets}
            className="p-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-full hover:scale-110 transition-transform"
          >
            <ArrowRightLeft className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* To Currency */}
        <div className="mb-4">
          <label className="text-sm text-gray-400 mb-2 block">To</label>
          <div className="bg-slate-900/50 border border-orange-500/20 rounded-lg p-4">
            <select
              value={toAsset}
              onChange={(e) => setToAsset(e.target.value)}
              className="w-full bg-transparent text-white outline-none mb-3"
            >
              {CURRENCIES.map(currency => (
                <option key={currency.symbol} value={currency.symbol} className="bg-slate-900">
                  {currency.name} ({currency.symbol})
                </option>
              ))}
            </select>
            
            <div className="text-2xl font-bold text-orange-400">
              {convertedAmount.toFixed(6)}
            </div>
            
            <div className="text-sm text-gray-400 mt-2">
              Balance: {balance?.balances ? (balance.balances[toAsset] || 0).toFixed(6) : '0.00'} {toAsset}
            </div>
          </div>
        </div>

        {/* Rate Display */}
        {conversionRate && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 mb-4 flex items-center justify-between">
            <span className="text-sm text-gray-400">Exchange Rate</span>
            <span className="text-sm font-semibold text-orange-400">
              1 {fromAsset} = {conversionRate.rate.toFixed(6)} {toAsset}
            </span>
          </div>
        )}

        {/* Convert Button */}
        <button
          onClick={handleConvert}
          disabled={isLoading || !amount || parseFloat(amount) <= 0}
          className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-600 text-white font-bold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Converting...' : 'Convert Now'}
        </button>
      </div>
    </div>
  );

  // Render history tab
  const renderHistoryTab = () => {
    if (conversionHistory.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500/20 rounded-full mb-4">
            <ArrowRightLeft className="w-8 h-8 text-orange-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">No Conversion History</h3>
          <p className="text-gray-400">Your completed conversions will appear here</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {conversionHistory.map((conversion) => (
          <div
            key={conversion.conversion_id}
            className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-orange-500/30 rounded-xl p-4 hover:border-orange-500/50 transition-all"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <CryptoIcon symbol={conversion.from_currency} size={32} />
                  <ArrowRightLeft className="w-4 h-4 text-orange-400" />
                  <CryptoIcon symbol={conversion.to_currency} size={32} />
                </div>
                <div>
                  <div className="font-semibold text-white">
                    {conversion.from_currency}/{conversion.to_currency}
                  </div>
                  <div className="text-xs text-gray-400">
                    {new Date(conversion.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded-full">
                  Completed
                </span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-gray-400 mb-1">Sent</div>
                <div className="font-semibold text-white">
                  {conversion.from_amount} {conversion.from_currency}
                </div>
              </div>
              <div>
                <div className="text-gray-400 mb-1">Received</div>
                <div className="font-semibold text-orange-400">
                  {conversion.to_amount.toFixed(6)} {conversion.to_currency}
                </div>
              </div>
              <div>
                <div className="text-gray-400 mb-1">Rate</div>
                <div className="font-semibold text-white">
                  {conversion.rate.toFixed(6)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation (2 tabs like Transfer.jsx) */}
      <div className="flex gap-2 bg-slate-900/50 p-2 rounded-xl border border-orange-500/20">
        <button
          onClick={() => setCurrentView('convert')}
          className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
            currentView === 'convert'
              ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-slate-800'
          }`}
        >
          Convert
        </button>
        <button
          onClick={() => setCurrentView('history')}
          className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
            currentView === 'history'
              ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-slate-800'
          }`}
        >
          History
        </button>
      </div>

      {/* Content */}
      {currentView === 'convert' && renderConvertTab()}
      {currentView === 'history' && renderHistoryTab()}
    </div>
  );
};

export default EnhancedCryptoConversion;
